import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, Mail, Check } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useSEO } from "@/lib/seo";

export default function ForgotPassword() {
  useSEO({ title: "Reset password", description: "Forgot your SonSpeak password? Send yourself a reset link.", path: "/forgot-password" });
  const [params] = useSearchParams();
  const [email, setEmail] = useState(params.get("email") || "");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      await api.post("/auth/forgot", { email: email.trim() });
      setSent(true);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
    setBusy(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-[color:var(--cream)]">
      <div className="brutal-card p-8 w-full max-w-md bg-[color:var(--paper)]" data-testid="forgot-card">
        <Link to="/login" className="pill-btn mb-5 text-sm"><ArrowLeft size={14} /> Back to sign in</Link>
        <div className="text-center mb-6">
          <div className="text-5xl mb-2" aria-hidden>🔑</div>
          <h1 className="display text-3xl font-extrabold">Reset password</h1>
          <p className="text-[color:var(--ink-soft)] mt-1">Type your email and we'll send a reset link.</p>
        </div>
        {sent ? (
          <div className="brutal-card bg-[#E5F4DD] p-5 text-center" data-testid="forgot-sent">
            <Check size={36} className="mx-auto mb-2 text-[color:var(--coral)]" />
            <h2 className="display text-xl font-extrabold mb-1">Check your inbox</h2>
            <p className="text-sm text-[color:var(--ink-soft)]">If that email is on SonSpeak, a reset link is on its way (good for 1 hour). Don't see it? Check spam.</p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <label className="block">
              <span className="display font-semibold mb-1 block">Email</span>
              <input className="brutal-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" data-testid="forgot-email" />
            </label>
            {error && <div className="brutal-card bg-[#FFE3E0] text-[color:var(--coral-deep)] p-3 text-sm font-bold" data-testid="forgot-error">{error}</div>}
            <button type="submit" className="pill-btn primary w-full" disabled={busy || !email.trim()} data-testid="forgot-submit">
              {busy ? "Sending…" : <><Mail size={14} /> Send reset link</>}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
